import React, { useState, useEffect, useRef, useMemo } from 'react';
import { NavLink, Link, useNavigate } from 'react-router-dom';
import { Menu, User, ShoppingBag, ChevronDown, ChevronUp, Search, X, PackageX, Heart } from 'lucide-react';
import { collection, query, where, getDocs, limit } from 'firebase/firestore';
import { db } from '../firebase/config';
import './Header.css';
import { useAuthContext } from '../hooks/useAuthContext'; 

// Use useMemo to memoize the debounced function
const useDebouncedCallback = (callback, delay) => {
  return useMemo(() => {
    let timeoutId;
    return (...args) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => callback(...args), delay);
    };
  }, [callback, delay]);
};

const Header = ({ toggleSidebar, logout }) => {
  const navigate = useNavigate();
  const [isMoreDropdownOpen, setIsMoreDropdownOpen] = useState(false);
  const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);
  const [isCategoriesDropdownOpen, setIsCategoriesDropdownOpen] = useState(false);
  const [isSearchBarOpen, setIsSearchBarOpen] = useState(false);
  const [bagItemsCount, setBagItemsCount] = useState(0);
  const [newOrdersCount, setNewOrdersCount] = useState(0);
  const [savedProductsCount, setSavedProductsCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [noResults, setNoResults] = useState(false);
  const { user } = useAuthContext();

  // Ref to handle click outside of search suggestions
  const searchContainerRef = useRef(null);
  const searchInputRef = useRef(null);

  // Categories data
  const categories = [
    { name: "Formal Shoes", path: "/collections/59WKAKnQQeWACLO3wqFE" },
    { name: "Formal Trouser", path: "/collections/6SPAe62irgkl14LL5trf" },
    { name: "Corporate Shirts", path: "/collections/V3HIjtJx6cAs31vofYLb" },
    { name: "Wristwatches", path: "/collections/YsBq10xHPVh3nnh6z97O" },
    { name: "Suits", path: "/collections/jfJ8K0XfDkeWK1yQ3P8n" },
    { name: "Sneakers", path: "/collections/oYdSojNc8PHgRp0d5Fzi" }
  ];

    // Fetch saved products count
    useEffect(() => {
      const fetchSavedProductsCount = async () => {
        if (!user) {
          setSavedProductsCount(0);
          return;
        }
  
        try {
          const savedProductsCollection = collection(db, 'savedProducts', user.uid, 'items');
          const savedProductsSnapshot = await getDocs(savedProductsCollection);
          setSavedProductsCount(savedProductsSnapshot.docs.length);
        } catch (error) {
          console.error('Error fetching saved products count:', error);
        }
      };
  
      fetchSavedProductsCount();
    }, [user]);

    // Fetch new orders count
    useEffect(() => {
      const fetchNewOrdersCount = async () => {
        if (!user) {
          setNewOrdersCount(0);
          return;
        }
  
        try {
          const ordersRef = collection(db, 'orders');
          const q = query(
            ordersRef,
            where('userId', '==', user.uid),
            where('isNew', '==', true)
          );
  
          const querySnapshot = await getDocs(q);
          setNewOrdersCount(querySnapshot.docs.length);
        } catch (error) {
          console.error('Error fetching new orders count:', error);
        }
      };
  
      fetchNewOrdersCount();
    }, [user]);

    // Fetch My Bag Count
  useEffect(() => {
    const fetchBagItemsCount = async () => {
      if (!user) {
        setBagItemsCount(0);
        return;
      }

      try {
        const itemsCollection = collection(db, 'myBag', user.uid, 'items');
        const itemsSnapshot = await getDocs(itemsCollection);
        const itemsCount = itemsSnapshot.docs.reduce((total, doc) => {
          const itemData = doc.data();
          return total + (itemData.quantity || 1);
        }, 0);
        setBagItemsCount(itemsCount);
      } catch (error) {
        console.error('Error fetching bag items count:', error);
      }
    };

    fetchBagItemsCount();
  }, [user]);


// Modified searchProducts function
const searchProducts = async (searchQuery) => {
  if (searchQuery.trim().length < 2) {
    setSearchResults([]);
    setNoResults(false);
    return;
  }

  setIsSearching(true);
  setNoResults(false);

  try {
    // Ensure case-insensitive search by using `.toLowerCase()`
    const q = query(
      collection(db, 'products'),
      where('nameLowerCase', '>=', searchQuery.toLowerCase()),
      where('nameLowerCase', '<=', searchQuery.toLowerCase() + '\uf8ff'),
      limit(5)
    );

    const querySnapshot = await getDocs(q);

    const results = querySnapshot.docs.map((doc) => ({
      id: doc.id, // Include document ID for navigation
      ...doc.data(),
    }));

    setSearchResults(results);
    setNoResults(results.length === 0);
  } catch (error) {
    console.error('Error searching products:', error);
    setNoResults(true);
  } finally {
    setIsSearching(false);
  }
};


      // Use the custom hook for debounced search
      const debouncedSearch = useDebouncedCallback(searchProducts, 300);

  // Handle search input changes
  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    debouncedSearch(query);
  };

  // Handle search input focus
  const handleSearchFocus = () => {
    if (searchQuery.trim().length >= 2) {
      debouncedSearch(searchQuery);
    }
  };

  // Handle search click
  const handleSearchClick = () => {
    setIsSearchBarOpen(true);
    // Focus the input when search is clicked
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  };

  // Handle search close
  const handleSearchClose = () => {
    setIsSearchBarOpen(false);
    setSearchQuery('');
    setSearchResults([]);
    setNoResults(false);
  };

  // Handle search submission
  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      // Navigate to search results page
      navigate(`/search?q=${encodeURIComponent(searchQuery)}`);
      handleSearchClose();
    }
  };

  // Handle click outside of search suggestions
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        searchContainerRef.current && 
        !searchContainerRef.current.contains(event.target) &&
        !event.target.closest('.search-icon')
      ) {
        handleSearchClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

// Modify navigateToProduct function to use the product ID directly
const navigateToProduct = (productId) => {
  navigate(`/product/${productId}`);
  handleSearchClose();
};

  return (
    <>
      <nav className="header-nav">
        <div className="header-container">
          {/* Mobile Menu Button */}
          <button onClick={toggleSidebar} className="mobile-menu-btn">
            <Menu size={24} />
          </button>

          {/* Brand Name */}
          <Link to="/" className="brand-name">
            XAN OMEGA
          </Link>

          {/* Desktop Navigation */}
          <div className="desktop-nav">
            {/* Your existing navigation items */}
            <NavLink to="/" className={({ isActive }) => (isActive ? 'nav-link active' : 'nav-link')}>
              Home
            </NavLink>
            
            {/* Categories dropdown */}
            <div className="dropdown">
              <button 
                className="dropdown-btn"
                onClick={() => setIsCategoriesDropdownOpen(!isCategoriesDropdownOpen)}
              >
                Collections
                {isCategoriesDropdownOpen ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
              </button>

              {isCategoriesDropdownOpen && (
                <div className="dropdown-menu">
                  {categories.map((category, index) => (
                    <NavLink 
                      key={index}
                      to={category.path}
                      className="dropdown-item"
                      onClick={() => setIsCategoriesDropdownOpen(false)}
                    >
                      {category.name}
                    </NavLink>
                  ))}
                </div>
              )}
            </div>

            {/* More Dropdown */}
            <div className="dropdown">
              <button onClick={() => setIsMoreDropdownOpen(!isMoreDropdownOpen)} className="dropdown-btn">
                More
                {isMoreDropdownOpen ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
              </button>

              {isMoreDropdownOpen && (
                <div className="dropdown-menu">
                  <NavLink to="/about" className="dropdown-item" onClick={() => setIsMoreDropdownOpen(false)}>
                    About Us
                  </NavLink>
                </div>
              )}
            </div>
            
            <NavLink to="/myorders" className={({ isActive }) => (isActive ? 'nav-link active' : 'nav-link')}>
              My Orders
              {newOrdersCount > 0 && (
                <span className="badge new-orders-badge">
                  {newOrdersCount}
                </span>
              )}
            </NavLink>
          </div>

          {/* Right Side Icons */}
          <div className="right-icons">
            {/* Search Icon */}
              <div className="search-icon" onClick={handleSearchClick}>
                <Search size={24} />
              </div>

               {/* Saved Products Icon */}
               <div className="saved-icon">
              <NavLink to="/saved-items" className="icon-link">
                <Heart size={24} />
                {savedProductsCount > 0 && (
                  <span className="badge saved-products-badge">
                    {savedProductsCount}
                  </span>
                )}
              </NavLink>
            </div>
            {/* Shopping Bag Icon */}
            <div className="shop-icon">
              <NavLink to="/mybag" className="icon-link">
                <ShoppingBag size={24} />
                {bagItemsCount > 0 && (
                  <span className="badge">
                    {bagItemsCount}
                  </span>
                )}
              </NavLink>
            </div>
            <div className="user-icon">
              {/* User Menu */}
              <div className="dropdown">
                <NavLink onClick={() => setIsUserDropdownOpen(!isUserDropdownOpen)} className="icon-link">
                  <User size={24} />
                </NavLink>

                {isUserDropdownOpen && (
                  <div className="dropdown-menu">
                      {user ? (
                      // User is logged in
                      <>
                            <NavLink to="/myaccount" className="dropdown-item" onClick={() => setIsUserDropdownOpen(false)}>
                              My Account
                            </NavLink>
                            <NavLink
                              onClick={() => {
                                logout();
                                navigate('/');
                                setIsUserDropdownOpen(false);
                              }}
                              className="dropdown-item"
                            >
                              Log Out
                            </NavLink>
                      </>
                    ) : (
                      // User is not logged in
                      <>
                            <NavLink to="/login" className="dropdown-item" onClick={() => setIsUserDropdownOpen(false)}>
                              Login
                            </NavLink>
                            <NavLink to="/signup" className="dropdown-item" onClick={() => setIsUserDropdownOpen(false)}>
                              Sign Up
                            </NavLink>
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        </nav>

{/* Search Bar Below Header */}
{isSearchBarOpen && (
  <div className="search-bar-wrapper" ref={searchContainerRef}>
    <form onSubmit={handleSearchSubmit} className="search-bar-container">
      <input
        ref={searchInputRef}
        type="text"
        className="search-input"
        placeholder="Search products..."
        value={searchQuery}
        onChange={handleSearchChange}
        onFocus={handleSearchFocus}
        autoFocus
      />
      <button 
        type="submit" 
        className="search-submit" 
        disabled={isSearching}
      >
        {isSearching ? (
      <div className="loading-container">
        <div className="loading-spinner-overlay">
        <div className="loading-spinner"></div>
        </div>
      </div>
        ) : (
          <Search size={20} />
        )}
      </button>
      <button 
        type="button" 
        className="search-close" 
        onClick={handleSearchClose}
      >
        <X size={20} />
      </button>
    </form>

    {/* Search Suggestions */}
    {(searchResults.length > 0 || noResults) && (
      <div className="search-suggestions">
        {noResults ? (
          <div className="no-results">
            <PackageX size={48} className="no-results-icon" />
            <p>No products found</p>
          </div>
        ) : (
          <ul>

            {searchResults.map((product) => (
              <li 
                key={product.id} 
                onClick={() => navigateToProduct(product.id)}
                className="search-suggestion-item"
              >
                <img 
                  src={product.images?.[0] || '/placeholder-image.png'} 
                  alt={product.name} 
                  className="suggestion-image"
                />
                <div className="suggestion-details">
                  <span className="suggestion-name">{product.name}</span>
                  <span className="product-category">
                    {product.category}
                  </span>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    )}
  </div>
)} 
</>
);
};

export default Header;